import type { AlertAppearance, Option } from '@ksyos/design-system';
import {
    Alert,
    Button,
    FormGroup,
    FormParagraph,
    Modal,
    Radio,
    RadioGroup,
    Select,
    TextInput,
} from '@ksyos/design-system';
import { pick } from 'lodash';
import { useEffect, useState } from 'react';
import type { OrganizationTypeWithLinkedProfessions } from '@common/enums';
import type { AccountRequestData, PractitionerMatch } from '@common/interfaces/practitionerMatchType';
import type { AccountRequestMatch } from '@common/validation/accountRequest';
import type { FieldRequirementsBasedOnProfession } from '@frontend/utils/checkRequiredAndAllowedAccountCreationFieldsPerProfession';
import type { ValidationErrors } from '@frontend/utils/validation';
import { Gender } from '@common/enums/gender';
import { MatchingEntityResultCode } from '@common/enums/matchingEntityResultCode';
import { Profession } from '@common/enums/profession';
import { formatString } from '@common/utils/formatString';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    fetchMatchingEntity,
    getSupervisors as getSupervisorsApi,
} from '@frontend/services/api/accountRequestsApiService';
import { getSelectedLanguage } from '@frontend/translate';
import {
    checkRequiredAndAllowedAccountCreationFieldsPerProfession,
    fieldRequirementsBasedOnProfessionInitialState,
} from '@frontend/utils/checkRequiredAndAllowedAccountCreationFieldsPerProfession';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import { hasAccountRequestAuthorization } from '@frontend/utils/hasAuthorization';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useDebounce } from '@frontend/utils/hooks/useDebounce';
import { IdentifierDeclaration } from '../userDataManagement/IdentifierDeclaration';
import { MatchingPractitionerTableSelector } from './MatchingPractitionerTableSelector';
import { SelectProfession } from './SelectProfession';
import { SpecialismSelector } from './SpecialismSelector';

export interface PractitionerAdd {
    emailAddress: string | null;
    gender: Gender | null;
    identifierDeclaration: string | null;
    identifierRegistrationBig: string | null;
    identifierRegistrationKp: string | null;
    nameFamily: string | null;
    nameGiven: string | null;
    nameInfix: string | null;
}

export interface EmploymentAdd {
    identifierDeclarationPractitionerId: string | null;
    profession: Profession | null;
    specialismCode?: string | null;
    zorgmailNummer: string | null;
}

export const emptyPractitionerAdd = {
    emailAddress: null,
    gender: null,
    identifierDeclaration: null,
    identifierRegistrationBig: null,
    identifierRegistrationKp: null,
    nameFamily: null,
    nameGiven: null,
    nameInfix: null,
};

export const emptyEmploymentAdd = {
    identifierDeclarationPractitionerId: null,
    profession: null,
    specialismCode: null,
    zorgmailNummer: null,
};

export interface PractitionerEmployment {
    employment: EmploymentAdd;
    practitioner: PractitionerAdd;
}

interface AccountCreationModalFieldsProps {
    errors: ValidationErrors;
    handleChangeNewAccount: (
        newAccount: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
    ) => void;
    handleModalSize?: (size: 'large' | 'xlarge') => void;
    handleSetExistingId: (matchingId?: AccountRequestMatch, newAccountFields?: PractitionerEmployment) => void;
    organizationId: string;
    organizationType: OrganizationTypeWithLinkedProfessions | null;
    validateMany: (
        keys: string[],
        updatedNewAccount?: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
    ) => void;
    validateOne: (
        key: string,
        updatedNewAccount?: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
    ) => void;
    willPractitionerUseCS: boolean | null;
}

type MatchingModalTitleAndText = {
    text?: string;
    title?: string;
};

export function AccountCreationModalFields(props: Readonly<AccountCreationModalFieldsProps>) {
    const abortSignal = useAbortSignal();
    const isAuthorizedKsyosUser = hasAccountRequestAuthorization('create', true);
    const genders = isAuthorizedKsyosUser
        ? [Gender.male, Gender.female, Gender.other, Gender.unknown]
        : [Gender.male, Gender.female, Gender.other];

    const [newAccountFields, setNewAccountFields] = useState<PractitionerEmployment>({
        employment: emptyEmploymentAdd,
        practitioner: emptyPractitionerAdd,
    });
    const [supervisorOptions, setSupervisorOptions] = useState<Option[]>([]);
    const [matchingPractitionerResultCode, setMatchingPractitionerResultCode] = useState<MatchingEntityResultCode>(
        MatchingEntityResultCode.NO_MATCH_FOUND
    );
    const [matchingPractitionerList, setMatchingPractitionerList] = useState<PractitionerMatch[]>([]);
    const [fieldRequirementsBasedOnProfession, setFieldRequirementsBasedOnProfession] =
        useState<FieldRequirementsBasedOnProfession>(fieldRequirementsBasedOnProfessionInitialState);
    const [isWarningModalOpen, setIsWarningModalOpen] = useState<boolean>(false);
    const [existingAccountIdInfo, setExistingAccountIdInfo] = useState<AccountRequestMatch | null>(null);
    const [matchingModalInfo, setMatchingModalInfo] = useState<MatchingModalTitleAndText>({});
    const [showKnownData, setShowKnownData] = useState<boolean>(false);
    const [supportAgbMatchAlert, setSupportAgbMatchAlert] = useState<
        { message: JSX.Element; type: AlertAppearance } | undefined
    >();
    const [supportNameAndGenderMatchAlert, setSupportNameAndGenderMatchAlert] = useState<
        { message: JSX.Element; type: AlertAppearance } | undefined
    >();

    const debounce = useDebounce(200);

    useEffect(() => {
        getSupervisorsOptions(props.organizationId);
    }, [props.organizationId]);

    useEffect(() => {
        handleChangePractitioner({ emailAddress: props.willPractitionerUseCS ? null : '' });
    }, [props.willPractitionerUseCS]);

    useEffect(() => {
        props.handleChangeNewAccount({ ...newAccountFields, matchingResultCode: matchingPractitionerResultCode });
    }, [newAccountFields]);

    useEffect(() => {
        updateModalFieldsBasedOnProfession(newAccountFields);
    }, [newAccountFields.employment.profession]);

    useEffect(() => {
        checkMatchingEntity(newAccountFields);
    }, [
        fieldRequirementsBasedOnProfession.isAgbCodeRequired,
        fieldRequirementsBasedOnProfession.isIdentifierRegistrationKpRequired,
    ]);

    useEffect(() => {
        checkMatchingEntity(newAccountFields);
    }, [newAccountFields.practitioner.identifierDeclaration, newAccountFields.practitioner.identifierRegistrationKp]);

    useEffect(() => {
        if (
            !showKnownData &&
            !newAccountFields.practitioner.identifierDeclaration &&
            newAccountFields.practitioner.nameFamily &&
            newAccountFields.practitioner.gender
        ) {
            checkMatchingEntity(newAccountFields);
        }
    }, [newAccountFields.practitioner.nameFamily, newAccountFields.practitioner.gender]);

    const translate = getSelectedLanguage();

    return (
        <>
            <FormGroup
                hasErrors={props.errors.has('employment.profession')}
                isRequired={true}
                label={translate.common.profession}
            >
                <SelectProfession
                    className="automation-account-creation-employment-profession"
                    error={getValidationErrorTranslation(props.errors.get('employment.profession'))}
                    onChange={(profession: Profession | null) => handleChangeEmployment('profession', profession)}
                    organizationType={props.organizationType}
                    placeholder=""
                    profession={newAccountFields.employment.profession}
                />
            </FormGroup>
            {fieldRequirementsBasedOnProfession.isAgbCodeOrSupervisorRequired && (
                <Alert appearance="info">
                    {`${translate.accountRequest.agbCodeOrSupervisorInfo} ${!isAuthorizedKsyosUser ? translate.accountRequest.contactSupportInfo : ''}`}
                </Alert>
            )}
            {fieldRequirementsBasedOnProfession.isIdentifierRegistrationKpRequired && (
                <FormGroup
                    hasErrors={props.errors.has('practitioner.identifierRegistrationKp')}
                    isRequired={true}
                    label={translate.identifierDeclarationOrRegistration.identifierRegistrationKp}
                >
                    <TextInput
                        data-automation-test="identifierRegistrationKp"
                        error={getValidationErrorTranslation(props.errors.get('practitioner.identifierRegistrationKp'))}
                        onBlur={() => props.validateOne('practitioner.identifierRegistrationKp')}
                        onChange={(e) =>
                            handleChangePractitioner({
                                identifierRegistrationKp: e.target.value ? e.target.value : null,
                            })
                        }
                        type="text"
                        value={newAccountFields.practitioner.identifierRegistrationKp ?? ''}
                    />
                </FormGroup>
            )}
            {fieldRequirementsBasedOnProfession.isSupervisorAllowed && (
                <FormGroup
                    hasErrors={props.errors.has('employment.identifierDeclarationPractitionerId')}
                    isRequired={
                        fieldRequirementsBasedOnProfession.isAgbCodeOrSupervisorRequired
                            ? !newAccountFields.practitioner.identifierDeclaration
                            : fieldRequirementsBasedOnProfession.isSupervisorRequired
                    }
                    label={
                        newAccountFields.employment.profession === Profession.generalPractitionerWaarnemer
                            ? translate.accountRequest.practiceHolder
                            : translate.supervisors.supervisor
                    }
                >
                    <Select
                        className="automation-account-creation-employment-supervisor"
                        error={getValidationErrorTranslation(
                            props.errors.get('employment.identifierDeclarationPractitionerId')
                        )}
                        isClearable={true}
                        onChange={(option) => {
                            const updatedAccountFields: PractitionerEmployment = {
                                ...newAccountFields,
                                employment: {
                                    ...newAccountFields.employment,
                                    identifierDeclarationPractitionerId: option?.value ?? null,
                                },
                            };
                            setNewAccountFields(updatedAccountFields);

                            if (fieldRequirementsBasedOnProfession.isAgbCodeOrSupervisorRequired) {
                                props.validateMany(
                                    [
                                        'practitioner.identifierDeclaration',
                                        'employment.identifierDeclarationPractitionerId',
                                    ],
                                    { ...updatedAccountFields, matchingResultCode: matchingPractitionerResultCode }
                                );
                            } else {
                                props.validateOne('employment.identifierDeclarationPractitionerId', {
                                    ...updatedAccountFields,
                                    matchingResultCode: matchingPractitionerResultCode,
                                });
                            }
                        }}
                        options={supervisorOptions}
                        placeholder=""
                        value={supervisorOptions.find(
                            (supervisor) =>
                                supervisor.value === newAccountFields.employment.identifierDeclarationPractitionerId
                        )}
                    />
                </FormGroup>
            )}
            {fieldRequirementsBasedOnProfession.isSpecialismFieldRequired && (
                <SpecialismSelector
                    className="automation-account-creation-employment-specialism"
                    errors={props.errors}
                    onChange={(value: string | null) => handleChangeEmployment('specialismCode', value)}
                    value={newAccountFields.employment.specialismCode ?? null}
                />
            )}

            {fieldRequirementsBasedOnProfession.isAgbCodeAllowed && (
                <IdentifierDeclaration
                    errors={props.errors}
                    hidePlaceholder={true}
                    isRequired={
                        fieldRequirementsBasedOnProfession.isAgbCodeOrSupervisorRequired
                            ? !newAccountFields.employment.identifierDeclarationPractitionerId
                            : fieldRequirementsBasedOnProfession.isAgbCodeRequired
                    }
                    onChange={async (name, value) => {
                        if (name === 'identifierDeclaration') {
                            handleChangePractitioner({ identifierDeclaration: value as string | null });
                            const updatedAccountFields: PractitionerEmployment & {
                                matchingResultCode: MatchingEntityResultCode;
                            } = {
                                ...newAccountFields,
                                matchingResultCode: matchingPractitionerResultCode,
                                practitioner: {
                                    ...newAccountFields.practitioner,
                                    identifierDeclaration: value as string | null,
                                },
                            };
                            if (fieldRequirementsBasedOnProfession.isAgbCodeOrSupervisorRequired) {
                                props.validateMany(
                                    [
                                        'practitioner.identifierDeclaration',
                                        'employment.identifierDeclarationPractitionerId',
                                    ],
                                    updatedAccountFields
                                );
                            } else {
                                props.validateOne('practitioner.identifierDeclaration', updatedAccountFields);
                            }
                        }
                    }}
                    value={newAccountFields.practitioner}
                />
            )}

            {showKnownData &&
                [
                    MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_EXTERNAL_ORGANIZATION,
                    MatchingEntityResultCode.PENDING_REQUEST_ON_EXTERNAL_ORGANIZATION,
                ].includes(matchingPractitionerResultCode) &&
                !isAuthorizedKsyosUser && (
                    <Alert appearance="info">
                        {translate.accountRequest.practitionerExistingAtExternalOrganizationAlertText}
                    </Alert>
                )}

            {!!supportAgbMatchAlert && (
                <Alert appearance={supportAgbMatchAlert.type}>{supportAgbMatchAlert.message}</Alert>
            )}

            {showKnownData && (
                <>
                    <FormGroup label={translate.common.name}>
                        <FormParagraph>
                            {getDisplayName(newAccountFields.practitioner, false, 'nameGivenNameFamily')}
                        </FormParagraph>
                    </FormGroup>
                    <FormGroup label={translate.common.gender}>
                        <FormParagraph>
                            {translate.genderEnum[newAccountFields.practitioner.gender as Gender]}
                        </FormParagraph>
                    </FormGroup>
                </>
            )}

            {fieldRequirementsBasedOnProfession.isIdentifierRegistrationBigRequired && (
                <FormGroup
                    hasErrors={props.errors.has('practitioner.identifierRegistrationBig')}
                    label={translate.identifierDeclarationOrRegistration.identifierRegistrationBig}
                >
                    <TextInput
                        data-automation-test="identifierRegistrationBig"
                        error={getValidationErrorTranslation(
                            props.errors.get('practitioner.identifierRegistrationBig')
                        )}
                        onBlur={() => props.validateOne('practitioner.identifierRegistrationBig')}
                        onChange={(e) =>
                            handleChangePractitioner({
                                identifierRegistrationBig: e.target.value ? e.target.value : null,
                            })
                        }
                        type="text"
                        value={newAccountFields.practitioner.identifierRegistrationBig ?? ''}
                    />
                </FormGroup>
            )}

            {fieldRequirementsBasedOnProfession.isZorgmailNummerFieldAllowed && (
                <FormGroup
                    hasErrors={props.errors.has('employment.zorgmailNummer')}
                    isRequired={fieldRequirementsBasedOnProfession.isZorgmailNummerFieldRequired}
                    label={translate.employment.zorgmailNummer}
                    tooltipText={!isAuthorizedKsyosUser ? translate.accountRequest.zorgmailNummerTooltip : undefined}
                >
                    <TextInput
                        data-automation-test="zorgmailNummer"
                        error={getValidationErrorTranslation(props.errors.get('employment.zorgmailNummer'))}
                        onChange={(e) =>
                            handleChangeEmployment('zorgmailNummer', e.target.value ? e.target.value : null)
                        }
                        type="text"
                        value={newAccountFields.employment.zorgmailNummer ?? ''}
                    />
                </FormGroup>
            )}

            {!showKnownData && (
                <>
                    <FormGroup
                        hasErrors={props.errors.has('practitioner.nameGiven')}
                        label={translate.common.nameGiven}
                    >
                        <TextInput
                            data-automation-test="nameGiven"
                            error={getValidationErrorTranslation(props.errors.get('practitioner.nameGiven'))}
                            onChange={(e) =>
                                handleChangePractitioner({ nameGiven: e.target.value ? e.target.value : null })
                            }
                            type="text"
                            value={newAccountFields.practitioner.nameGiven ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={props.errors.has('practitioner.nameInfix')}
                        label={translate.common.nameInfix2}
                    >
                        <TextInput
                            data-automation-test="nameInfix"
                            error={getValidationErrorTranslation(props.errors.get('practitioner.nameInfix'))}
                            onChange={(e) =>
                                handleChangePractitioner({ nameInfix: e.target.value ? e.target.value : null })
                            }
                            type="text"
                            value={newAccountFields.practitioner.nameInfix ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={props.errors.has('practitioner.nameFamily')}
                        isRequired
                        label={translate.common.nameFamily}
                    >
                        <TextInput
                            data-automation-test="nameFamily"
                            error={getValidationErrorTranslation(props.errors.get('practitioner.nameFamily'))}
                            onBlur={async (e) => {
                                const nextElement = e.relatedTarget;
                                if (!nextElement || nextElement.getAttribute('data-key') !== 'cancel') {
                                    props.validateOne('practitioner.nameFamily');
                                }
                            }}
                            onChange={(e) => handleChangePractitioner({ nameFamily: e.target.value })}
                            type="text"
                            value={newAccountFields.practitioner.nameFamily ?? ''}
                        />
                    </FormGroup>
                    <FormGroup
                        hasErrors={props.errors.has('practitioner.gender')}
                        isRequired
                        label={translate.common.gender}
                    >
                        <RadioGroup
                            data-automation-test="gender"
                            error={getValidationErrorTranslation(props.errors.get('practitioner.gender'))}
                            layout="column"
                            name="gender"
                            onChange={async (event) => {
                                handleChangePractitioner({ gender: event.target.value as Gender });
                                props.validateOne('practitioner.gender', {
                                    ...newAccountFields,
                                    matchingResultCode: matchingPractitionerResultCode,
                                    practitioner: {
                                        ...newAccountFields.practitioner,
                                        gender: event.target.value as Gender,
                                    },
                                });
                            }}
                            value={newAccountFields.practitioner.gender ?? ''}
                        >
                            {genders.map((gender) => (
                                <Radio
                                    key={gender}
                                    label={translate.genderEnum[gender]}
                                    value={gender}
                                />
                            ))}
                        </RadioGroup>
                    </FormGroup>
                </>
            )}

            {!!matchingPractitionerList?.length && (
                <MatchingPractitionerTableSelector
                    alertTextWithMatches={{
                        title: translate.accountRequest.selectMatchingPractitionerInfoAlert,
                    }}
                    alertTextWithoutMatches={{
                        title: translate.accountRequest.reviewByKsyos.alertWithoutMatches.title,
                    }}
                    onSelectMatch={(selectedPractitioner) =>
                        handleMatchingPractitionerTableSelect(
                            matchingPractitionerList,
                            newAccountFields.practitioner,
                            newAccountFields,
                            selectedPractitioner
                        )
                    }
                    practitionerMatches={matchingPractitionerList}
                />
            )}

            {!!supportNameAndGenderMatchAlert && (
                <Alert appearance={supportNameAndGenderMatchAlert.type}>{supportNameAndGenderMatchAlert.message}</Alert>
            )}
            {props.willPractitionerUseCS && (
                <FormGroup
                    hasErrors={
                        props.errors.has('practitioner.emailAddress') || props.errors.has('employment.emailAddress')
                    }
                    isRequired={!existingAccountIdInfo?.hasPractitionerEmailAddress}
                    label={translate.accountRequest.workEmail}
                >
                    <TextInput
                        data-automation-test="emailAddress"
                        error={getValidationErrorTranslation(
                            props.errors.get('practitioner.emailAddress') ?? props.errors.get('employment.emailAddress')
                        )}
                        onBlur={(e) => {
                            const nextElement = e.relatedTarget;
                            if (!nextElement || nextElement.getAttribute('data-key') !== 'cancel') {
                                props.validateOne('practitioner.emailAddress');
                                props.validateOne('employment.emailAddress');
                            }
                        }}
                        onChange={(e) => {
                            handleChangePractitioner({ emailAddress: e.target.value ? e.target.value : null });
                            props.validateOne('practitioner.emailAddress', {
                                ...newAccountFields,
                                matchingResultCode: matchingPractitionerResultCode,
                                practitioner: {
                                    ...newAccountFields.practitioner,
                                    emailAddress: e.target.value ? e.target.value : null,
                                },
                            });
                        }}
                        type="text"
                        value={newAccountFields.practitioner.emailAddress ?? ''}
                    />
                </FormGroup>
            )}
            {renderMatchingModal()}
        </>
    );
    async function getSupervisorsOptions(organizationId: string) {
        const shouldFetchOnlyActiveSupervisors = !isAuthorizedKsyosUser;
        const supervisors = await getSupervisorsApi(organizationId, shouldFetchOnlyActiveSupervisors, abortSignal);

        const options: Option[] = supervisors.items.map(({ displayName, id }) => ({ label: displayName, value: id }));

        setSupervisorOptions(options);
    }

    function updateModalFieldsBasedOnProfession(accountFields: PractitionerEmployment) {
        const fieldRequirements = checkRequiredAndAllowedAccountCreationFieldsPerProfession(
            accountFields.employment.profession,
            props.organizationType
        );
        setFieldRequirementsBasedOnProfession(fieldRequirements);

        setNewAccountFields({
            employment: {
                ...accountFields.employment,
                identifierDeclarationPractitionerId: fieldRequirements.isSupervisorAllowed
                    ? accountFields.employment.identifierDeclarationPractitionerId
                    : null,
                specialismCode: fieldRequirements.isSpecialismFieldRequired
                    ? accountFields.employment.specialismCode
                    : null,
                zorgmailNummer: fieldRequirements.isZorgmailNummerFieldAllowed
                    ? accountFields.employment.zorgmailNummer
                    : null,
            },
            practitioner: {
                ...accountFields.practitioner,
                identifierDeclaration: fieldRequirements.isAgbCodeAllowed
                    ? accountFields.practitioner.identifierDeclaration
                    : null,
                identifierRegistrationBig: fieldRequirements.isIdentifierRegistrationBigRequired
                    ? accountFields.practitioner.identifierRegistrationBig
                    : null,
                identifierRegistrationKp: fieldRequirements.isIdentifierRegistrationKpRequired
                    ? accountFields.practitioner.identifierRegistrationKp
                    : null,
            },
        });
    }

    function handleChangePractitioner(
        newPractitionerUpdate: Partial<PractitionerAdd>,
        accountFields = newAccountFields
    ) {
        setNewAccountFields({
            ...accountFields,
            practitioner: { ...accountFields.practitioner, ...newPractitionerUpdate },
        });
    }

    function handleChangeEmployment<K extends keyof EmploymentAdd>(name: K, value: EmploymentAdd[K]) {
        setNewAccountFields({
            ...newAccountFields,
            employment: { ...newAccountFields.employment, [name]: value },
        });

        props.validateOne(`employment.${name}`, {
            ...newAccountFields,
            employment: {
                ...newAccountFields.employment,
                [name]: value,
            },
            matchingResultCode: matchingPractitionerResultCode,
        });
    }

    async function checkMatchingEntity(accountFields: PractitionerEmployment) {
        debounce(async () => {
            let updatedPractitionerFields = accountFields['practitioner'];

            if (showKnownData || supportAgbMatchAlert) {
                const resultingFields = clearKnownData(accountFields.practitioner);
                updatedPractitionerFields = { ...resultingFields };
            }

            const result = await fetchMatchingEntity(
                {
                    gender: updatedPractitionerFields.gender,
                    identifierDeclaration: updatedPractitionerFields.identifierDeclaration,
                    identifierRegistrationKp: updatedPractitionerFields.identifierRegistrationKp,
                    isRegistrationCodeRequiredForProfession:
                        fieldRequirementsBasedOnProfession.isAgbCodeRequired ||
                        fieldRequirementsBasedOnProfession.isIdentifierRegistrationKpRequired,
                    nameFamily: updatedPractitionerFields.nameFamily,
                    organizationId: props.organizationId,
                },
                abortSignal
            );

            props.handleModalSize?.(result.matchingPractitioners.length ? 'xlarge' : 'large');
            setMatchingPractitionerList(result.matchingPractitioners);
            setMatchingPractitionerResultCode(result.matchingCode);

            const updatedAccountFields = {
                ...accountFields,
                matchingResultCode: result.matchingCode,
                practitioner: { ...updatedPractitionerFields },
            };

            props.handleChangeNewAccount(updatedAccountFields);

            handleExistingAccountId(null, updatedAccountFields, result.accountRequestData);

            handleMatchingPractitionerAlert(
                isAuthorizedKsyosUser,
                result.matchingCode,
                updatedAccountFields.practitioner.identifierDeclaration,
                updatedAccountFields.practitioner.identifierRegistrationKp,
                updatedAccountFields,
                result.accountRequestData
            );
        });
    }

    function renderMatchingModal() {
        return (
            <Modal
                isOpen={isWarningModalOpen}
                onClose={() => setIsWarningModalOpen(false)}
                size="medium"
            >
                <Modal.Header>{matchingModalInfo.title}</Modal.Header>
                <Modal.Body>
                    <Alert appearance="warning">{matchingModalInfo.text}</Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setIsWarningModalOpen(false)}>{translate.common.closeShort}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    function determineMatchingModalTitleAndText(
        matchingCode: MatchingEntityResultCode,
        identifierDeclaration: string | null,
        identifierRegistrationKp: string | null,
        matchingRequestData?: AccountRequestData
    ) {
        switch (matchingCode) {
            case MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION:
                return {
                    text:
                        identifierDeclaration || identifierRegistrationKp
                            ? translate.accountRequest.practitionerDuplicatedRegistrationCodeModalTextForGp
                            : translate.accountRequest.practitionerDuplicatedNameAndGenderModalTextForGp,
                    title: translate.accountRequest.practitionerDuplicatedModalTitle,
                };
            case MatchingEntityResultCode.PENDING_REQUEST_ON_SAME_ORGANIZATION:
                return {
                    text: matchingRequestData?.approverPractitionerName
                        ? formatString(translate.accountRequest.practitionerAwaitingApprovalTextForGp, [
                              matchingRequestData?.approverPractitionerName,
                          ])
                        : translate.accountRequest.practitionerAwaitingApprovalFromKsyosTextForGp,
                    title: translate.accountRequest.practitionerAwaitingApprovalModalTitle,
                };
            default:
                return;
        }
    }

    function handleMatchingPractitionerAlert(
        userIsSupport: boolean,
        matchingCode: MatchingEntityResultCode,
        identifierDeclaration: string | null,
        identifierRegistrationKp: string | null,
        accountFields: PractitionerEmployment,
        accountRequestData?: AccountRequestData
    ) {
        if (userIsSupport) {
            const alertResult = getSupportMatchAlertContent(
                matchingCode,
                identifierDeclaration,
                identifierRegistrationKp,
                accountRequestData?.id
            );

            if (alertResult?.source === 'registrationCode') {
                setSupportAgbMatchAlert({ message: alertResult.message, type: alertResult.type });
            } else if (alertResult?.source === 'nameAndGender') {
                setSupportNameAndGenderMatchAlert({ message: alertResult.message, type: alertResult.type });
            } else {
                setSupportAgbMatchAlert(undefined);
                setSupportNameAndGenderMatchAlert(undefined);
            }

            if (
                ![MatchingEntityResultCode.NAME_GENDER_MATCH, MatchingEntityResultCode.NO_MATCH_FOUND].includes(
                    matchingCode
                )
            ) {
                setKnownData(accountFields, accountRequestData);
            }
        } else {
            const shouldShowInfoAlertPendingAtExternalOrganization =
                [
                    MatchingEntityResultCode.PENDING_REQUEST_ON_EXTERNAL_ORGANIZATION,
                    MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_EXTERNAL_ORGANIZATION,
                ].includes(matchingCode) &&
                (!!identifierDeclaration || !!identifierRegistrationKp);

            if (shouldShowInfoAlertPendingAtExternalOrganization) {
                setKnownData(accountFields, accountRequestData);
            }

            const modalNeedsToBeDisplayed = [
                MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION,
                MatchingEntityResultCode.PENDING_REQUEST_ON_SAME_ORGANIZATION,
            ].includes(matchingCode);

            if (modalNeedsToBeDisplayed) {
                const modalDataResult = determineMatchingModalTitleAndText(
                    matchingCode,
                    identifierDeclaration,
                    identifierRegistrationKp,
                    accountRequestData
                );

                if (modalDataResult) {
                    setMatchingModalInfo(modalDataResult);
                    setIsWarningModalOpen(true);
                }
            }
        }
    }

    function setKnownData(accountFields: PractitionerEmployment, matchingPractitionerData?: AccountRequestData) {
        const necessaryDataForUpdate = pick(matchingPractitionerData, [
            'nameFamily',
            'nameGiven',
            'nameInfix',
            'gender',
        ]);

        if (necessaryDataForUpdate && Object.values(necessaryDataForUpdate).some((value) => !!value)) {
            setShowKnownData(true);
            handleChangePractitioner(necessaryDataForUpdate, accountFields);
        } else {
            setShowKnownData(false);
            setExistingAccountIdInfo(null);
        }
    }

    function getSupportMatchAlertContent(
        matchingCode: MatchingEntityResultCode,
        identifierDeclaration: string | null,
        identifierRegistrationKp: string | null,
        accountRequestId?: string
    ): { message: JSX.Element; source: 'registrationCode' | 'nameAndGender'; type: AlertAppearance } | undefined {
        switch (matchingCode) {
            case MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_SAME_ORGANIZATION:
                if (identifierDeclaration || identifierRegistrationKp) {
                    return {
                        message: <>{translate.accountRequest.registrationCodeExistsAtSameOrganizationAlert}</>,
                        source: 'registrationCode',
                        type: 'warning',
                    };
                } else {
                    return {
                        message: <>{translate.accountRequest.nameAndGenderExistAtSameOrganizationAlert}</>,
                        source: 'nameAndGender',
                        type: 'warning',
                    };
                }
            case MatchingEntityResultCode.PENDING_REQUEST_ON_SAME_ORGANIZATION:
                if (accountRequestId) {
                    return {
                        message: (
                            <>
                                {translate.accountRequest.waitingForApprovalOnAgbSameOrganizationAlertFirst}
                                <Alert.Link
                                    to={`/accountRequest/${accountRequestId}`}
                                    variant="link"
                                >
                                    account
                                </Alert.Link>
                                {translate.accountRequest.waitingForApprovalOnAgbSameOrganizationAlertSecond}
                            </>
                        ),
                        source: 'registrationCode',
                        type: 'warning',
                    };
                }
                break;
            case MatchingEntityResultCode.PENDING_REQUEST_ON_EXTERNAL_ORGANIZATION:
            case MatchingEntityResultCode.ACTIVE_EMPLOYMENT_ON_EXTERNAL_ORGANIZATION:
                return {
                    message: <>{translate.accountRequest.registrationCodeExistsAtExternalOrganizationAlert}</>,
                    source: 'registrationCode',
                    type: 'info',
                };
            case MatchingEntityResultCode.NAME_GENDER_MATCH:
                if (accountRequestId) {
                    return {
                        message: (
                            <>
                                {translate.accountRequest.waitingForApprovalOnNameAndGenderSameOrganizationAlertFirst}
                                <Alert.Link
                                    to={`/accountRequest/${accountRequestId}`}
                                    variant="link"
                                >
                                    account
                                </Alert.Link>{' '}
                                {translate.accountRequest.waitingForApprovalOnNameAndGenderSameOrganizationAlertSecond}
                            </>
                        ),
                        source: 'nameAndGender',
                        type: 'warning',
                    };
                }
                break;
            case MatchingEntityResultCode.NO_MATCH_FOUND:
            default:
                return undefined;
        }
    }

    function handleExistingAccountId(
        selectedPractitioner: AccountRequestMatch | null,
        accountFields: PractitionerEmployment,
        requestData?: AccountRequestData
    ) {
        function submitChanges(matchingIdInfo: AccountRequestMatch | null) {
            props.handleSetExistingId(matchingIdInfo ?? undefined, accountFields);
            setExistingAccountIdInfo(matchingIdInfo);
        }

        if (selectedPractitioner) {
            submitChanges(selectedPractitioner);
        } else if (requestData?.practitionerId || requestData?.matchingAccountRequestId || requestData?.id) {
            if (requestData?.practitionerId) {
                submitChanges({
                    entity: 'practitionerId',
                    hasPractitionerEmailAddress: !!requestData?.practitionerEmailAddress,
                    id: requestData?.practitionerId,
                });
            } else {
                submitChanges({
                    entity: 'matchingAccountRequestId',
                    hasPractitionerEmailAddress: !!requestData?.practitionerEmailAddress,
                    id: (requestData?.matchingAccountRequestId ?? requestData?.id)!,
                });
            }
        } else {
            submitChanges(null);
        }
    }

    function clearKnownData(practitionerFields: PractitionerEmployment['practitioner'], keepNameAndGender?: boolean) {
        let updatedPractitionerFields = { ...practitionerFields };

        if (showKnownData) {
            updatedPractitionerFields = {
                ...updatedPractitionerFields,
                nameGiven: null,
                nameInfix: null,
            };

            if (!keepNameAndGender) {
                updatedPractitionerFields = { ...updatedPractitionerFields, gender: null, nameFamily: null };
            }

            handleChangePractitioner(updatedPractitionerFields);
        }

        setShowKnownData(false);
        setSupportAgbMatchAlert(undefined);

        return updatedPractitionerFields;
    }

    function handleMatchingPractitionerTableSelect(
        practitionerList: PractitionerMatch[],
        currentPractitioner: PractitionerAdd,
        accountFields: PractitionerEmployment,
        selectedPractitioner: AccountRequestMatch | null
    ) {
        handleExistingAccountId(selectedPractitioner, accountFields);

        if (selectedPractitioner) {
            const selectedPractitionerData = practitionerList.find(
                (practitioner) =>
                    practitioner.practitionerId === selectedPractitioner?.id ||
                    practitioner.accountRequestId === selectedPractitioner?.id
            );
            setKnownData(accountFields, selectedPractitionerData as AccountRequestData);
        } else {
            clearKnownData(currentPractitioner, true);
            setKnownData(accountFields);
        }
    }
}
